import { FAQAccordion, type FAQItem } from "@/components/public/FAQAccordion"
import { LandingFooter } from "@/components/public/LandingFooter"
import { PublicHeader } from "@/components/public/PublicHeader"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { BreathingGradient } from "@/components/ui/living-brand"
import { SpotlightCard } from "@/components/ui/SpotlightCard"
import { Heading, Text } from "@/components/ui/typography"
import { cn } from "@/lib/utils"
import { semanticTokens } from "@supabank/config/theme"
import { Page } from "@supabank/ui"
import { ArrowRight, CheckCircle2, RefreshCw, Shield, Wallet2 } from "lucide-react"
import { useEffect } from "react"
import { Link } from "react-router-dom"

// Bridge API Supported Assets - https://apidocs.bridge.xyz/
// Fiat via Virtual Accounts: USD (ACH/Wire), EUR (SEPA), MXN (SPEI), BRL (PIX Beta)
// Crypto via Custodial Wallets: USDC, USDT, EURC, PYUSD, USDB, DAI, USDP
const supportedCurrencies = [
  // Fiat currencies (via Virtual Accounts)
  { name: "USD", type: "Fiat", description: "US Dollar via ACH/Wire" },
  { name: "EUR", type: "Fiat", description: "Euro via SEPA" },
  { name: "MXN", type: "Fiat", description: "Mexican Peso via SPEI" },
  { name: "BRL", type: "Fiat", description: "Brazilian Real via PIX (Beta)" },
  // Stablecoins (via Custodial Wallets)
  { name: "USDC", type: "Crypto", description: "USD Coin on 8 chains" },
  { name: "USDT", type: "Crypto", description: "Tether on Ethereum/Tron" },
  { name: "EURC", type: "Crypto", description: "Euro Coin on Base/Solana" },
  { name: "PYUSD", type: "Crypto", description: "PayPal USD on Ethereum/Solana" },
  { name: "USDB", type: "Crypto", description: "Bridge native on Base/Solana" },
  { name: "DAI", type: "Crypto", description: "Dai on Ethereum" },
  { name: "USDP", type: "Crypto", description: "Pax Dollar on Solana" },
]

const features = [
  {
    title: "Multi-Currency Balances",
    description: "Hold multiple currencies in one wallet. View all balances in real-time.",
    icon: <Wallet2 className="h-6 w-6" />,
  },
  {
    title: "Real-Time Updates",
    description: "Balances update instantly when transactions complete. No refresh needed.",
    icon: <RefreshCw className="h-6 w-6" />,
  },
  {
    title: "Transaction History",
    description: "Complete transaction history with status tracking, timestamps, and details.",
    icon: <CheckCircle2 className="h-6 w-6" />,
  },
  {
    title: "Secure Custodial Storage",
    description: "Institutional-grade security. Your funds are held in Bridge custodial wallets.",
    icon: <Shield className="h-6 w-6" />,
  },
]

const useCases = [
  {
    title: "Hold Multiple Currencies",
    description: "Keep fiat (USD, EUR, MXN, BRL) and stablecoins (USDC, USDT, EURC) in one place. No need for separate accounts.",
  },
  {
    title: "Track Transactions",
    description: "View complete transaction history with real-time status updates and details.",
  },
  {
    title: "Multi-Chain Support",
    description: "Send and receive stablecoins on Ethereum, Solana, Polygon, Base, Arbitrum, Optimism, Avalanche, Stellar, and Tron.",
  },
]

const faqItems: FAQItem[] = [
  {
    id: "what-is",
    q: "What is a Custodial Wallet?",
    a: "A multi-currency cryptocurrency wallet managed by Supabank/Bridge. You own the wallet and control the funds, but private keys are held securely by Bridge (custodial). This provides institutional-grade security while maintaining full control over your funds.",
  },
  {
    id: "currencies",
    q: "Which currencies are supported?",
    a: "We support USD, EUR, MXN, and BRL (fiat via Virtual Accounts), plus USDC, USDT, EURC, PYUSD, USDB, DAI, and USDP (stablecoins via Custodial Wallets). Stablecoins are available on 9 chains: Ethereum, Solana, Polygon, Base, Arbitrum, Optimism, Avalanche, Stellar, and Tron.",
  },
  {
    id: "security",
    q: "Is it secure?",
    a: "Yes. All wallets are custodial and managed by Bridge API with institutional-grade security. Your funds are held in secure, insured custodial wallets. We use KYC verification, automatic compliance checks, and all transactions are monitored.",
  },
  {
    id: "fees",
    q: "Are there fees?",
    a: "There are no monthly fees or account maintenance fees. Internal transfers between your wallets are free. External transactions (deposits, withdrawals) may have network fees or conversion fees (typically 0.5-1% plus 2% developer fee) depending on the transaction type.",
  },
  {
    id: "access",
    q: "How do I access my wallet?",
    a: "After completing KYC, your wallet is automatically created. You can access it through the Supabank dashboard, view balances, transaction history, and manage your funds. All wallets are linked to your verified account.",
  },
  {
    id: "transfers",
    q: "Can I transfer funds between wallets?",
    a: "Yes, you can transfer funds between your own wallets instantly with zero fees. Transfers are processed immediately and appear in your transaction history.",
  },
]

export default function WalletPage() {
  useEffect(() => {
    // SEO: Update page title and meta
    document.title = "Multi-Currency Wallet - USD, EUR, MXN, BRL, USDC, USDT | Supabank"

    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Hold fiat (USD, EUR, MXN, BRL) and stablecoins (USDC, USDT, EURC) in one secure custodial wallet. Multi-chain support for 9 blockchains. Real-time balances and instant transfers.')
    }

    // Structured Data (JSON-LD)
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "Multi-Currency Wallet",
      "description": "Custodial cryptocurrency wallet that supports multiple currencies (USD, EUR, USDC, USDT, and more) with real-time balance updates and transaction history",
      "brand": {
        "@type": "Brand",
        "name": "Supabank"
      },
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      }
    }

    const softwareAppData = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Supabank Wallet",
      "applicationCategory": "FinanceApplication",
      "operatingSystem": "Web",
      "description": "Multi-currency custodial wallet for holding and managing USD, EUR, USDC, USDT, and other cryptocurrencies",
      "offers": {
        "@type": "Offer",
        "price": "0"
      }
    }

    const faqData = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqItems.map(item => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.a
        }
      }))
    }

    // Remove existing structured data scripts
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]')
    existingScripts.forEach(script => {
      try {
        const data = JSON.parse(script.textContent || '{}')
        if (data['@type'] === 'Product' || data['@type'] === 'SoftwareApplication' || data['@type'] === 'FAQPage') {
          script.remove()
        }
      } catch (e) {
        // Ignore parse errors
      }
    })

    // Add new structured data
    const script1 = document.createElement('script')
    script1.type = 'application/ld+json'
    script1.textContent = JSON.stringify(structuredData)
    document.head.appendChild(script1)

    const script2 = document.createElement('script')
    script2.type = 'application/ld+json'
    script2.textContent = JSON.stringify(softwareAppData)
    document.head.appendChild(script2)

    const script3 = document.createElement('script')
    script3.type = 'application/ld+json'
    script3.textContent = JSON.stringify(faqData)
    document.head.appendChild(script3)

    return () => {
      script1.remove()
      script2.remove()
      script3.remove()
    }
  }, [])

  return (
    <Page data-ux-contract="canon" role="main" className="bg-background text-foreground">
      {/* Unified PublicHeader component */}
      <PublicHeader />

      <main className="space-y-0" id="main">
        {/* Hero Section */}
        <section className={cn(
          "relative overflow-hidden",
          "py-12 sm:py-16 md:py-20 lg:py-24",
          // CANON: Hero gradient background (lavenderGlow for fintech feel)
          semanticTokens.heroGradient.lavenderGlow.tailwind,
          // Dark mode variant
          "dark:bg-black dark:bg-[radial-gradient(ellipse_at_20%_50%,hsla(270,95%,75%,0.08)_0%,transparent_50%),radial-gradient(ellipse_at_80%_80%,hsla(217,91%,60%,0.06)_0%,transparent_50%),hsl(var(--background))]"
        )}>
          {/* Living Brand: Breathing Gradient (syncs with LED heartbeat) - DISABLED on dark theme */}
          <BreathingGradient
            variant="lavender"
            syncWithLed
            className="absolute inset-0 z-[1] pointer-events-none opacity-30 dark:hidden"
          />
          <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6">
            <div className="space-y-6 sm:space-y-8 text-center">
              <div className={cn("inline-flex items-center justify-center rounded-full border px-4 py-2 text-sm font-medium", semanticTokens.border.default, semanticTokens.fg.muted, semanticTokens.radius.full)}>
                <Wallet2 className="mr-2 h-4 w-4" />
                Product
              </div>
              <Heading level={1}>
                Multi-Currency Wallet
              </Heading>
              <Text variant="lg" color="muted" className="mx-auto max-w-3xl leading-relaxed">
                Hold USD, EUR, USDC, USDT, and more in one secure wallet. View balances, track transactions, and transfer funds instantly with real-time updates.
              </Text>
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <Button asChild size="lg" className="min-h-[44px] touch-manipulation">
                  <Link to="/waitlist">
                    See Your Wallet
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="min-h-[44px] touch-manipulation">
                  <Link to="/how-payments-work">
                    Learn How It Works
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className={cn(
          "relative overflow-hidden py-12 sm:py-16 md:py-20",
          semanticTokens.harmony.calm.bg
        )}>
          {/* Color Lab: Subtle Blue-Lavender gradient */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `
                radial-gradient(ellipse at 10% 90%, hsla(217, 91%, 60%, 0.10) 0%, transparent 45%),
                radial-gradient(ellipse at 90% 10%, hsla(270, 95%, 75%, 0.08) 0%, transparent 45%)
              `
            }}
          />
          <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6">
            <div className="space-y-8 sm:space-y-10">
              <div className="text-center space-y-3">
                <Heading level={2}>
                  Key Features
                </Heading>
                <Text color="muted" className="mx-auto max-w-2xl">
                  Everything you need to manage your multi-currency balances.
                </Text>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
                {features.map((feature) => (
                  <SpotlightCard
                    key={feature.title}
                    className={cn(
                      "p-6 sm:p-8 border",
                      semanticTokens.bg.surface,
                      semanticTokens.border.default
                    )}
                  >
                    <div className="flex items-start gap-4">
                      <div className={cn("rounded-lg p-3", semanticTokens.bg.muted)}>
                        {feature.icon}
                      </div>
                      <div className="space-y-2 flex-1">
                        <h3 className={cn("text-lg font-bold", semanticTokens.fg.default)}>
                          {feature.title}
                        </h3>
                        <p className={cn("text-sm leading-relaxed", semanticTokens.fg.muted)}>
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </SpotlightCard>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Supported Currencies */}
        <section className={cn("py-12 sm:py-16 md:py-20", semanticTokens.bg.surface)}>
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="space-y-8 sm:space-y-10">
              <div className="text-center space-y-3">
                <Heading level={2}>
                  Supported Currencies
                </Heading>
                <Text color="muted" className="mx-auto max-w-2xl">
                  Hold multiple currencies in one wallet. All balances update in real-time.
                </Text>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
                {supportedCurrencies.map((currency) => (
                  <SpotlightCard
                    key={currency.name}
                    className={cn(
                      "p-5 sm:p-6 border text-center",
                      semanticTokens.bg.surface,
                      semanticTokens.border.default
                    )}
                  >
                    <div className="space-y-2">
                      <div className="flex items-center justify-center gap-2">
                        <h3 className={cn("text-lg font-bold", semanticTokens.fg.default)}>
                          {currency.name}
                        </h3>
                        <Badge variant="outline" className="text-xs">
                          {currency.type}
                        </Badge>
                      </div>
                      <p className={cn("text-xs leading-relaxed", semanticTokens.fg.muted)}>
                        {currency.description}
                      </p>
                    </div>
                  </SpotlightCard>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className={cn(
          "relative overflow-hidden py-12 sm:py-16 md:py-20",
          semanticTokens.harmony.calm.bg
        )}>
          {/* Color Lab: Subtle Blue-Lavender gradient */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `
                radial-gradient(ellipse at 10% 90%, hsla(217, 91%, 60%, 0.10) 0%, transparent 45%),
                radial-gradient(ellipse at 90% 10%, hsla(270, 95%, 75%, 0.08) 0%, transparent 45%)
              `
            }}
          />
          <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6">
            <div className="space-y-8 sm:space-y-10">
              <div className="text-center space-y-3">
                <Heading level={2} className={cn("sm:text-display-sm md:text-display-md", semanticTokens.fg.default)}>
                  Use Cases
                </Heading>
                <p className={cn("mx-auto max-w-2xl text-base sm:text-lg", semanticTokens.fg.muted)}>
                  Perfect for managing multiple currencies in one place.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
                {useCases.map((useCase) => (
                  <SpotlightCard
                    key={useCase.title}
                    className={cn(
                      "p-6 sm:p-8 border",
                      semanticTokens.bg.surface,
                      semanticTokens.border.default
                    )}
                  >
                    <div className="space-y-3">
                      <h3 className={cn("text-lg font-bold", semanticTokens.fg.default)}>
                        {useCase.title}
                      </h3>
                      <p className={cn("text-sm leading-relaxed", semanticTokens.fg.muted)}>
                        {useCase.description}
                      </p>
                    </div>
                  </SpotlightCard>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Security */}
        <section className={cn("py-12 sm:py-16 md:py-20", semanticTokens.bg.surface)}>
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="space-y-6 sm:space-y-8">
              <div className="text-center space-y-3">
                <Heading level={2} className={cn("sm:text-display-sm md:text-display-md", semanticTokens.fg.default)}>
                  Security & Compliance
                </Heading>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
                <SpotlightCard className={cn("p-6 sm:p-8 border", semanticTokens.bg.surface, semanticTokens.border.default)}>
                  <div className="flex items-start gap-4">
                    <div className={cn("rounded-lg p-3", semanticTokens.bg.muted)}>
                      <Shield className={cn("h-6 w-6", semanticTokens.fg.default)} />
                    </div>
                    <div className="space-y-2 flex-1">
                      <h3 className={cn("text-lg font-bold", semanticTokens.fg.default)}>
                        Custodial Security
                      </h3>
                      <p className={cn("text-sm leading-relaxed", semanticTokens.fg.muted)}>
                        All wallets are custodial and managed by Bridge API with institutional-grade security. Your funds are held in secure, insured custodial wallets.
                      </p>
                    </div>
                  </div>
                </SpotlightCard>

                <SpotlightCard className={cn("p-6 sm:p-8 border", semanticTokens.bg.surface, semanticTokens.border.default)}>
                  <div className="flex items-start gap-4">
                    <div className={cn("rounded-lg p-3", semanticTokens.bg.muted)}>
                      <CheckCircle2 className={cn("h-6 w-6", semanticTokens.fg.default)} />
                    </div>
                    <div className="space-y-2 flex-1">
                      <h3 className={cn("text-lg font-bold", semanticTokens.fg.default)}>
                        Automatic Compliance
                      </h3>
                      <p className={cn("text-sm leading-relaxed", semanticTokens.fg.muted)}>
                        KYC verification, automatic compliance checks, and transaction monitoring. All transactions are screened before funds move.
                      </p>
                    </div>
                  </div>
                </SpotlightCard>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className={cn(
          "relative overflow-hidden py-12 sm:py-16 md:py-20",
          semanticTokens.harmony.calm.bg
        )}>
          {/* Color Lab: Subtle Lavender gradient */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `
                radial-gradient(ellipse at 80% 20%, hsla(270, 95%, 75%, 0.08) 0%, transparent 45%),
                radial-gradient(ellipse at 20% 80%, hsla(217, 91%, 60%, 0.06) 0%, transparent 45%)
              `
            }}
          />
          <div className="relative z-10 mx-auto max-w-4xl px-4 sm:px-6">
            <div className="space-y-8 sm:space-y-10">
              <div className="text-center space-y-3">
                <Heading level={2} className={cn("sm:text-display-sm md:text-display-md", semanticTokens.fg.default)}>
                  Frequently Asked Questions
                </Heading>
              </div>
              <FAQAccordion items={faqItems} />
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className={cn("py-12 sm:py-16 md:py-20", semanticTokens.bg.surface)}>
          <div className="mx-auto max-w-4xl px-4 sm:px-6 text-center space-y-6">
            <Heading level={2} className={cn("sm:text-display-sm md:text-display-md", semanticTokens.fg.default)}>
              Ready to Get Started?
            </Heading>
            <p className={cn("text-base sm:text-lg", semanticTokens.fg.muted)}>
              Get your multi-currency wallet and start managing your funds today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="min-h-[44px] touch-manipulation">
                <Link to="/waitlist">
                  See Your Wallet
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="min-h-[44px] touch-manipulation">
                <Link to="/products">
                  View All Products
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <LandingFooter />
    </Page>
  )
}

